declare global {
  interface HTMLElement {
    slider?: Slider;
  }
}

interface BasicConfig {
  slidesPerView?: number;
  spaceBetween?: number;
  speed?: number;
}

export enum Breakpoints {
  Xxs = 400,
  Xs = 576,
  Sm = 768,
  Md = 992,
  Lg = 1200,
  Xl = 1400
}

type BreakpointConfig = {
  [K in Breakpoints]?: BasicConfig;
};

interface SliderConfig extends BasicConfig {
  sliderContainer: HTMLElement;
  showFrom?: Breakpoints;
  hideFrom?: Breakpoints;
  updateHeight?: {
    always?: boolean;
    updateUntil?: Breakpoints;
    updateFrom?: Breakpoints;
  };
  breakpoints?: BreakpointConfig;
  pagination?: {
    elementSelector: string;
    buttonWrapperClass?: string;
    buttonWrapperActiveClass?: string;
    buttonWrapperElement?: string;
    buttonTexts?: string[];
  };
  customPagination?: {
    selector: string;
    activeClass: string;
    buttonWrapperElement?: string;
  };
  navigation?: {
    prevButtonSelector: string;
    nextButtonSelector: string;
    disabledClass?: string;
  };
  accessibility?: {
    slideRole?: string;
    containerMessage?: string;
    slideLabelMessage?: string;
  };
  autoslide?: {
    enabled: boolean;
    interval?: number;
  };
}

type SliderEventType = 'slideChange' | 'reset';
type SliderEventCallback = (slider: Slider) => void;

const DEFAULT_SLIDES_PER_VIEW = 1;
const DEFAULT_SPACE_BETWEEN = 24;
const DEFAULT_SPEED = 500;

class Slider {
  private hasRecalculatedOnInteraction: boolean = false;
  private sliderConfig: SliderConfig;
  private sliderContainer: HTMLElement;
  private slidesWrapper: HTMLElement | null;
  public slides: NodeListOf<HTMLElement> | [];
  public activeSlideIndex: number = 0;
  private pagination?: {
    wrapper: HTMLElement | null;
    buttonWrapperClass?: string;
    buttonWrapperActiveClass?: string;
    buttonWrapperElement?: string;
    buttonTexts?: string[];
    buttons: NodeListOf<HTMLButtonElement> | null;
  };
  private customPagination?: {
    selector: string;
    activeClass: string;
    buttons: NodeListOf<HTMLButtonElement> | null;
  };
  private autoslide?: {
    disableAfterInteraction: boolean;
    interval: number;
    sliderObserver: IntersectionObserver | null;
    intervalId: ReturnType<typeof setInterval> | null;
    pauseHandler: (() => void) | null;
    resumeHandler: (() => void) | null;
  };
  public prevButton: HTMLButtonElement | null;
  public nextButton: HTMLButtonElement | null;
  private disabledClass: string | null;
  private slidesPerView: number = DEFAULT_SLIDES_PER_VIEW;
  private spaceBeetwen: number = DEFAULT_SPACE_BETWEEN;
  private speed: number = DEFAULT_SPEED;

  private touchStartHandler: (e: TouchEvent) => void;
  private touchMoveHandler: (e: TouchEvent) => void;
  private touchEndHandler: (e: TouchEvent) => void;
  private isTouching: boolean = false;
  private touchMoveX: number = 0;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private touchEndX: number = 0;
  private touchEndY: number = 0;
  private touchStartTime: number = 0;
  private minSwipeDistance: number = 50;
  private minSwipeVelocity: number = 0.5;
  private boundaryResistance: number = 0.3;

  private eventListeners: Map<SliderEventType, SliderEventCallback[]> = new Map();

  constructor(sliderConfig: SliderConfig) {
    this.sliderConfig = sliderConfig;
    this.sliderContainer = sliderConfig.sliderContainer;
    this.slidesWrapper = this.sliderContainer.querySelector<HTMLElement>('.slider-wrapper, .swiper-wrapper') || null;
    this.slides = this.slidesWrapper?.querySelectorAll<HTMLElement>('.slide, .swiper-slide') || [];
    if (sliderConfig.pagination) {
      this.pagination = {
        wrapper: this.sliderContainer.querySelector(sliderConfig.pagination.elementSelector),
        buttonWrapperClass: sliderConfig.pagination.buttonWrapperClass,
        buttonWrapperActiveClass: sliderConfig.pagination.buttonWrapperActiveClass,
        buttonWrapperElement: sliderConfig.pagination.buttonWrapperElement,
        buttonTexts: sliderConfig.pagination.buttonTexts,
        buttons: null
      };
    }

    if (sliderConfig.customPagination) {
      this.customPagination = {
        selector: sliderConfig.customPagination.selector,
        activeClass: sliderConfig.customPagination.activeClass,
        buttons: null
      };
    }

    if (sliderConfig.autoslide?.enabled) {
      this.autoslide = {
        disableAfterInteraction: false,
        interval: sliderConfig.autoslide.interval || 6000,
        sliderObserver: null,
        intervalId: null,
        pauseHandler: null,
        resumeHandler: null
      };
    }

    this.prevButton = sliderConfig.navigation ? this.sliderContainer.querySelector<HTMLButtonElement>(sliderConfig.navigation.prevButtonSelector) : null;
    this.nextButton = sliderConfig.navigation ? this.sliderContainer.querySelector<HTMLButtonElement>(sliderConfig.navigation.nextButtonSelector) : null;
    this.disabledClass = sliderConfig.navigation?.disabledClass || null;

    this.touchStartHandler = (e: TouchEvent) => this.handleTouchStart(e);
    this.touchMoveHandler = (e: TouchEvent) => this.handleTouchMove(e);
    this.touchEndHandler = (e: TouchEvent) => this.handleTouchEnd(e);

    if (!this.slidesWrapper || !this.slides.length) {
      return;
    }

    if (sliderConfig.accessibility?.containerMessage) {
      this.slidesWrapper.setAttribute('aria-label', sliderConfig.accessibility.containerMessage);
    }

    if (this.prevButton) {
      this.prevButton.classList.toggle('is-hidden', !this.checkSliderGeneralBreakpoints());
      this.prevButton.addEventListener('click', () => this.slidePrev());
      if (!this.prevButton.ariaLabel) {
        this.prevButton.ariaLabel = en ? 'Previous slide' : 'Vorherige Slide';
      }
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle('is-hidden', !this.checkSliderGeneralBreakpoints());
      this.nextButton.addEventListener('click', () => this.slideNext());
      if (!this.nextButton.ariaLabel) {
        this.nextButton.ariaLabel = en ? 'Next slide' : 'Nächste Slide';
      }
    }

    const breakpointValues = Object.values(Breakpoints)
      .filter(value => typeof value === 'number')
      .sort((a, b) => (a as number) - (b as number)) as number[];

    breakpointValues.forEach(breakpoint => {
      const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
      const handler = () => this.toggleSlider(this.checkSliderGeneralBreakpoints());
      if (mq.addEventListener) {
        mq.addEventListener('change', handler);
      } else {
        (mq as any).addListener(handler);
      }
    });

    this.toggleSlider(this.checkSliderGeneralBreakpoints());
  }

  private async initSlider() {
    this.sliderContainer.closest('section')?.classList.add('slider-overflow-hidden');
    this.sliderContainer.classList.add('slider-container');
    this.slidesWrapper!.classList.add('wrapper-initialized');
    this.updateConfig();

    await this.waitForImagesToLoad();

    this.applyStyles();
    this.disableButtons();
    this.addTouchListeners();
    this.prepareContainerHeightUpdate(this.checkSliderUpdateHeightBreakpoints());

    if (this.pagination?.wrapper) {
      this.createPagination();
    }

    if (this.customPagination) {
      this.initCustomPaginationButtons();
    }

    if (this.autoslide) {
      this.setAutoSlide();
    }

    this.slide();

    this.addRecalculationListeners();
  }

  private async waitForImagesToLoad(): Promise<void> {
    const visibleSlideCount = Math.ceil(this.slidesPerView);
    const visibleSlides = Array.from(this.slides).slice(this.activeSlideIndex, this.activeSlideIndex + visibleSlideCount);

    const images: HTMLImageElement[] = [];
    visibleSlides.forEach(slide => {
      images.push(...Array.from(slide.querySelectorAll<HTMLImageElement>('img')));
    });
    if (images.length === 0) {
      return;
    }

    const imagePromises = images.map(img => {
      if (img.complete) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve, reject) => {
        img.addEventListener('load', () => resolve(), { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
        setTimeout(() => resolve(), 2000);
      });
    });

    await Promise.all(imagePromises);
  }

  private recalculateStyles(): void {
    if (this.hasRecalculatedOnInteraction) {
      return;
    }
    this.hasRecalculatedOnInteraction = true;
    this.applyStyles();
  }

  private resetSlider(): void {
    this.activeSlideIndex = 0;
    this.slidesWrapper!.style.transform = `translateX(0px)`;
    this.initSlider();
    this.emit('reset');
  }

  private updateConfig(): void {
    this.slidesPerView = this.sliderConfig.slidesPerView || DEFAULT_SLIDES_PER_VIEW;
    this.spaceBeetwen = this.sliderConfig.spaceBetween !== undefined ? this.sliderConfig.spaceBetween : DEFAULT_SPACE_BETWEEN;
    this.speed = this.sliderConfig.speed || DEFAULT_SPEED;

    if (this.sliderConfig.breakpoints) {
      for (const [breakpoint, config] of Object.entries(this.sliderConfig.breakpoints)) {
        if (window.matchMedia(`(min-width: ${parseInt(breakpoint)}px)`).matches) {
          this.slidesPerView = config.slidesPerView || DEFAULT_SLIDES_PER_VIEW;
          this.spaceBeetwen = config.spaceBetween !== undefined ? config.spaceBetween : DEFAULT_SPACE_BETWEEN;
          this.speed = config.speed || DEFAULT_SPEED;
        }
      }
    }

    this.slidesWrapper!.style.transition = `transform ${this.speed}ms ease`;
  }

  private removePagination(): void {
    this.pagination?.wrapper?.querySelectorAll(this.pagination?.buttonWrapperElement ? this.pagination.buttonWrapperElement : '.slider-pagination-bullet').forEach(elem => elem.remove());
  }

  private createPagination(): void {
    this.removePagination();
    for (let i = 0; i < this.slides.length - (Math.floor(this.slidesPerView) - 1); i++) {
      const paginationButton = document.createElement('button');
      if (!this.pagination?.buttonWrapperElement) {
        paginationButton.classList.add('slider-pagination-bullet');
        paginationButton.classList.toggle('slider-pagination-bullet-active', i === this.activeSlideIndex);
        paginationButton.ariaLabel = en ? `Go to slider page ${i + 1}` : `Gehe zu Slider-Seite ${i + 1}`;
      }
      paginationButton.addEventListener('click', () => this.slide(i));

      if (this.pagination?.buttonWrapperElement) {
        const paginationButtonWrapper = document.createElement(this.pagination?.buttonWrapperElement);
        paginationButtonWrapper.classList.add(this.pagination?.buttonWrapperClass || '');
        paginationButtonWrapper.classList.toggle(this.pagination?.buttonWrapperActiveClass || '', i === this.activeSlideIndex);
        paginationButton.innerHTML = this.pagination?.buttonTexts?.[i] || 'Element ' + (i + 1);
        paginationButtonWrapper.appendChild(paginationButton);
        this.pagination?.wrapper?.appendChild(paginationButtonWrapper);
      } else {
        this.pagination?.wrapper?.appendChild(paginationButton);
      }
    }
    if (this.pagination) {
      this.pagination.buttons =
        this.pagination.wrapper?.querySelectorAll(this.pagination?.buttonWrapperElement ? this.pagination.buttonWrapperElement + ' > button' : '.slider-pagination-bullet') || null;
    }
  }

  private initCustomPaginationButtons(): void {
    if (!this.customPagination) {
      return;
    }

    this.customPagination.buttons = this.sliderContainer.querySelectorAll(this.customPagination.selector + ' button') || null;
    this.customPagination.buttons?.forEach((button, index) => {
      button.addEventListener('click', () => this.slide(index));
    });
  }

  private applyStyles(retryCount: number = 0): void {
    const calculatedSlideWidth = this.getSlideStyles();

    this.slides.forEach((slide, index) => {
      slide.classList.add('slide-initialized');
      if (this.sliderConfig.accessibility?.slideRole) {
        slide.setAttribute('role', this.sliderConfig.accessibility.slideRole);
      }

      if (this.sliderConfig.accessibility?.slideLabelMessage) {
        slide.setAttribute('aria-label', this.sliderConfig.accessibility.slideLabelMessage.replace('{{index}}', String(index + 1)).replace('{{slidesLength}}', String(this.slides.length)));
      }

      slide.style.marginRight = index === this.slides.length - 1 ? '0' : `${this.spaceBeetwen}px`;
      slide.style.width = `${calculatedSlideWidth}px`;
      slide.style.maxWidth = `${this.slidesWrapper!.offsetWidth}px`;
      slide.classList.toggle(
        'slide-visible',
        (index >= this.activeSlideIndex && index <= this.activeSlideIndex + Math.ceil(this.slidesPerView) - 1) || (index + 1 === this.slides.length - 1 && this.slidesPerView % 1 !== 0)
      );
      slide.addEventListener('focus', e => {
        if (e.target && (e.target as HTMLElement).matches(':focus-visible')) {
          this.slide(index);
          this.slides.forEach(sli => sli.removeAttribute('tabindex'));
        }
      });
    });

    this.validateSlideWidths(calculatedSlideWidth, retryCount);
  }

  private validateSlideWidths(slideWidth: number, retryCount: number = 0): void {
    const containerWidth = this.slidesWrapper!.offsetWidth;
    const totalGaps = this.spaceBeetwen * (Math.floor(this.slidesPerView) - 1);
    const totalSlidesWidth = slideWidth * this.slides.length + totalGaps;

    if (totalSlidesWidth > containerWidth * this.slides.length && retryCount < 3) {
      requestAnimationFrame(() => {
        this.applyStyles(retryCount + 1);
      });
    }
  }

  private getSlideStyles(): number {
    const containerWidth = this.slidesWrapper!.offsetWidth;
    const gapsTotal = this.spaceBeetwen * (Math.floor(this.slidesPerView) - 1);
    const slideWidth = (containerWidth - gapsTotal) / this.slidesPerView;
    const maxSlideWidth = containerWidth;
    return Math.min(slideWidth, maxSlideWidth);
  }

  private getMaxTranslateBoundaries(): { min: number; max: number } {
    const slideWidth = this.getSlideStyles();
    const maxTranslate = 0;
    const minTranslate = -(slideWidth * this.slides.length + this.spaceBeetwen * (this.slides.length - 1) - slideWidth * this.slidesPerView);
    return { min: minTranslate, max: maxTranslate };
  }

  private clampSlideIndex(index: number): number {
    const maxIndex = this.slides.length - Math.floor(this.slidesPerView);
    return Math.max(0, Math.min(index, maxIndex));
  }

  public slidePrev(): void {
    this.activeSlideIndex = this.clampSlideIndex(this.activeSlideIndex - 1);
    this.slide();
  }

  public slideNext(): void {
    this.activeSlideIndex = this.clampSlideIndex(this.activeSlideIndex + 1);
    this.slide();
  }

  public slide(index?: number): void {
    if (index !== undefined) {
      this.activeSlideIndex = index;
    }

    this.activeSlideIndex = this.clampSlideIndex(this.activeSlideIndex);

    if (this.activeSlideIndex > this.slides.length - Math.floor(this.slidesPerView) || this.activeSlideIndex < 0) {
      return;
    }

    this.slides.forEach((slide, index) => {
      slide.classList.toggle(
        'slide-visible',
        (index >= this.activeSlideIndex && index <= this.activeSlideIndex + Math.ceil(this.slidesPerView) - 1) || (index + 1 === this.slides.length - 1 && this.slidesPerView % 1 !== 0)
      );
      slide.setAttribute('tabindex', index === this.activeSlideIndex ? '0' : '-1');
    });

    if (this.pagination?.buttons) {
      this.pagination.buttons.forEach((button, index) => {
        const element = this.pagination?.buttonWrapperElement ? button.parentElement : button;
        element?.classList.toggle(this.pagination?.buttonWrapperElement ? this.pagination?.buttonWrapperActiveClass || '' : 'slider-pagination-bullet-active', index === this.activeSlideIndex);
      });
    }

    if (this.customPagination?.buttons) {
      this.customPagination.buttons.forEach((button, index) => {
        button.classList.toggle('active', index === this.activeSlideIndex);
      });
    }

    this.slidesWrapper!.style.transform = `translateX(-${
      this.activeSlideIndex !== this.slides.length - 1
        ? (this.getSlideStyles() + this.spaceBeetwen) * this.activeSlideIndex
        : this.getSlideStyles() * this.slides.length + this.spaceBeetwen * (this.slides.length - 1) - this.getSlideStyles() * this.slidesPerView
    }px)`;

    if (this.checkSliderUpdateHeightBreakpoints()) {
      requestAnimationFrame(() => {
        this.updateContainerHeight();
      });
    }

    this.disableButtons();
    this.emit('slideChange');
  }

  private disableButtons(): void {
    if (this.nextButton) {
      this.nextButton.disabled = this.activeSlideIndex === this.slides.length - Math.floor(this.slidesPerView);
      if (this.disabledClass) {
        this.nextButton.classList.toggle(this.disabledClass, this.nextButton.disabled);
      }
    }

    if (this.prevButton) {
      this.prevButton.disabled = this.activeSlideIndex === 0;
      if (this.disabledClass) {
        this.prevButton.classList.toggle(this.disabledClass, this.prevButton.disabled);
      }
    }
  }

  private handleTouchStart(e: TouchEvent): void {
    this.isTouching = true;
    this.touchStartX = e.changedTouches[0].screenX;
    this.touchStartY = e.changedTouches[0].screenY;
    this.touchStartTime = Date.now();
    this.slidesWrapper!.style.transition = 'none';
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.isTouching) return;

    this.touchMoveX = e.changedTouches[0].screenX;
    const touchMoveY = e.changedTouches[0].screenY;
    const deltaX = this.touchMoveX - this.touchStartX;
    const deltaY = touchMoveY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();

      const currentTranslate = -((this.getSlideStyles() + this.spaceBeetwen) * this.activeSlideIndex);
      let newTranslate = currentTranslate + deltaX;

      const boundaries = this.getMaxTranslateBoundaries();

      if (newTranslate > boundaries.max) {
        const overflow = newTranslate - boundaries.max;
        newTranslate = boundaries.max + overflow * this.boundaryResistance;
      } else if (newTranslate < boundaries.min) {
        const overflow = boundaries.min - newTranslate;
        newTranslate = boundaries.min - overflow * this.boundaryResistance;
      }

      this.slidesWrapper!.style.transform = `translateX(${newTranslate}px)`;
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.isTouching) return;

    this.isTouching = false;
    this.touchEndX = e.changedTouches[0].screenX;
    this.touchEndY = e.changedTouches[0].screenY;
    this.slidesWrapper!.style.transition = `transform ${this.speed}ms ease`;
    this.handleSwipe();
  }

  private handleSwipe(): void {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const touchDuration = Date.now() - this.touchStartTime;
    const velocity = Math.abs(deltaX) / (touchDuration || 1);

    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isSignificantDistance = Math.abs(deltaX) > this.minSwipeDistance;
    const isSignificantVelocity = velocity > this.minSwipeVelocity;

    if (isHorizontalSwipe && (isSignificantDistance || isSignificantVelocity)) {
      if (this.autoslide) {
        this.removeAutoSlide();
        this.autoslide.disableAfterInteraction = true;
      }
      if (deltaX > 0) {
        this.activeSlideIndex > 0 ? this.slidePrev() : this.slide();
      } else {
        this.activeSlideIndex < this.slides.length - Math.floor(this.slidesPerView) ? this.slideNext() : this.slide();
      }
    } else {
      this.slide();
    }
  }

  private addTouchListeners(): void {
    this.slidesWrapper!.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    this.slidesWrapper!.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    this.slidesWrapper!.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  private removeTouchListeners(): void {
    this.slidesWrapper!.removeEventListener('touchstart', this.touchStartHandler);
    this.slidesWrapper!.removeEventListener('touchmove', this.touchMoveHandler);
    this.slidesWrapper!.removeEventListener('touchend', this.touchEndHandler);
  }

  private checkSliderGeneralBreakpoints(): boolean {
    if (this.sliderConfig.showFrom && window.matchMedia(`(max-width: ${this.sliderConfig.showFrom}px)`).matches) {
      return false;
    }

    if (this.sliderConfig.hideFrom && window.matchMedia(`(min-width: ${this.sliderConfig.hideFrom}px)`).matches) {
      return false;
    }

    return true;
  }

  private checkSliderUpdateHeightBreakpoints(): boolean {
    if (!this.sliderConfig.updateHeight) {
      return false;
    }

    if (this.sliderConfig.updateHeight.always) {
      return true;
    }

    if (this.sliderConfig.updateHeight.updateUntil && window.matchMedia(`(max-width: ${this.sliderConfig.updateHeight.updateUntil}px)`).matches) {
      return true;
    }

    if (this.sliderConfig.updateHeight.updateFrom && window.matchMedia(`(min-width: ${this.sliderConfig.updateHeight.updateFrom}px)`).matches) {
      return true;
    }

    return false;
  }

  private addRecalculationListeners(): void {
    const recalculate = () => {
      this.recalculateStyles();
      window.removeEventListener('mousemove', recalculate);
      window.removeEventListener('touchstart', recalculate);
    };

    window.addEventListener('mousemove', recalculate, { once: true });
    window.addEventListener('touchstart', recalculate, { once: true });
  }

  public toggleSlider(showSlider: boolean): void {
    if (showSlider) {
      this.prevButton?.classList.remove('is-hidden');
      this.nextButton?.classList.remove('is-hidden');
      this.resetSlider();
    }

    if (!showSlider) {
      this.hideSlider();
    }
  }

  public hideSlider(): void {
    this.sliderContainer.closest('section')?.classList.remove('slider-overflow-hidden');
    this.sliderContainer.classList.remove('slider-container');
    this.removeTouchListeners();
    this.slidesWrapper!.style.removeProperty('transform');
    this.slidesWrapper!.style.removeProperty('transition');
    this.slidesWrapper!.classList.remove('wrapper-initialized');
    this.removePagination();
    this.prevButton?.classList.add('is-hidden');
    this.nextButton?.classList.add('is-hidden');
    this.removeAutoSlide();
    this.slides.forEach(slide => {
      slide.classList.remove('slide-initialized');
      slide.style.removeProperty('width');
      slide.style.removeProperty('margin-right');
      slide.removeAttribute('role');
      slide.removeAttribute('aria-label');
      slide.removeAttribute('tabindex');
      slide.classList.remove('slide-visible');
    });
  }

  public on(event: SliderEventType, callback: SliderEventCallback): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)?.push(callback);
  }

  public off(event: SliderEventType, callback: SliderEventCallback): void {
    const callbacks = this.eventListeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  private emit(event: SliderEventType): void {
    const callbacks = this.eventListeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(this));
    }
  }

  private prepareContainerHeightUpdate(updateHeight: boolean): void {
    this.slidesWrapper!.classList.toggle('slider-wrapper-height-update', updateHeight);
    const initTransitionValue = this.slidesWrapper!.style.getPropertyValue('transition');
    const heightTransition = ', height 300ms';
    if (updateHeight) {
      this.slidesWrapper!.style.setProperty('transition', initTransitionValue + heightTransition);
      this.updateContainerHeight();
    } else {
      this.slidesWrapper!.style.removeProperty('height');
      this.slidesWrapper!.style.setProperty('transition', initTransitionValue.replace(heightTransition, ''));
    }
  }

  public updateContainerHeight(): void {
    const activeSlide = this.slides[this.activeSlideIndex];
    this.slidesWrapper!.style.height = activeSlide.offsetHeight + 'px';
  }

  private setAutoSlide(): void {
    if (!this.autoslide || this.autoslide.sliderObserver || this.autoslide?.disableAfterInteraction) {
      return;
    }

    const setSlideInterval = () => {
      return setInterval(() => {
        this.activeSlideIndex === this.slides.length - Math.floor(this.slidesPerView) ? this.slide(0) : this.slideNext();
      }, this.autoslide!.interval);
    };

    const startInterval = () => {
      if (this.autoslide?.disableAfterInteraction) {
        return;
      }
      this.autoslide!.intervalId = setSlideInterval();
    };

    const pauseInterval = () => {
      if (this.autoslide?.intervalId) {
        clearInterval(this.autoslide!.intervalId);
        this.autoslide!.intervalId = null;
      }
    };

    if (!this.autoslide.sliderObserver) {
      this.autoslide.sliderObserver = new IntersectionObserver(entries => {
        const isIntersecting = entries[0].isIntersecting;

        if (isIntersecting && !this.autoslide?.intervalId) {
          startInterval();
        } else if (!isIntersecting && this.autoslide?.intervalId) {
          pauseInterval();
        }
      });
    }

    if (!this.autoslide?.disableAfterInteraction && this.autoslide.sliderObserver) {
      this.autoslide.sliderObserver.observe(this.sliderContainer);

      this.autoslide.pauseHandler = () => pauseInterval();
      this.autoslide.resumeHandler = () => startInterval();

      ['mouseenter', 'focusin'].forEach(event => {
        this.sliderContainer.addEventListener(event, this.autoslide!.pauseHandler!);
      });

      ['mouseleave', 'focusout'].forEach(event => {
        this.sliderContainer.addEventListener(event, this.autoslide!.resumeHandler!);
      });

      [...(this.customPagination?.buttons || []), ...(this.pagination?.buttons || []), ...(this.prevButton ? [this.prevButton] : []), ...(this.nextButton ? [this.nextButton] : [])].forEach(button => {
        button.addEventListener('click', () => {
          this.removeAutoSlide();
          this.autoslide!.disableAfterInteraction = true;
        });
      });
    }
  }

  private removeAutoSlide(): void {
    if (!this.autoslide) {
      return;
    }

    if (this.autoslide.sliderObserver) {
      this.autoslide.sliderObserver.disconnect();
      this.autoslide.sliderObserver = null;
    }

    if (this.autoslide.intervalId) {
      clearInterval(this.autoslide.intervalId);
      this.autoslide.intervalId = null;
    }

    if (this.autoslide.pauseHandler) {
      ['mouseenter', 'focusin'].forEach(event => {
        this.sliderContainer.removeEventListener(event, this.autoslide!.pauseHandler!);
      });
      this.autoslide.pauseHandler = null;
    }

    if (this.autoslide.resumeHandler) {
      ['mouseleave', 'focusout'].forEach(event => {
        this.sliderContainer.removeEventListener(event, this.autoslide!.resumeHandler!);
      });
      this.autoslide.resumeHandler = null;
    }
  }
}

document.querySelectorAll<HTMLElement>('.mag-posts-slider').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    slidesPerView: 1.1,
    spaceBetween: 20,
    speed: 500,
    breakpoints: {
      768: {
        slidesPerView: 2,
        spaceBetween: 24
      },
      992: {
        slidesPerView: parseInt(slider.dataset.slidesMd || '3'),
        spaceBetween: 40
      }
    },
    pagination: {
      elementSelector: '.slider-pagination'
    },
    navigation: {
      prevButtonSelector: '.button-prev',
      nextButtonSelector: '.button-next',
      disabledClass: 'disabled'
    },
    accessibility: {
      slideRole: 'link',
      slideLabelMessage: en ? 'element {{index}} of {{slidesLength}}' : 'Element {{index}} von {{slidesLength}}',
      containerMessage: en ? 'Similar articles' : 'Ähnliche Artikel'
    }
  });
});

document.querySelectorAll<HTMLElement>('.tariff-options-slider').forEach(slider => {
  if (slider.slider) {
    return;
  }
  const sliderWrapper = slider.querySelector('.slider-wrapper');
  let matchMedia = 1200;
  if (sliderWrapper?.classList.contains('columns-4')) {
    matchMedia = 992;
  } else if (sliderWrapper?.classList.contains('columns-2')) {
    matchMedia = 768;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    hideFrom: matchMedia,
    slidesPerView: 1.1,
    spaceBetween: 20,
    speed: 500,
    pagination: {
      elementSelector: '.slider-pagination'
    },
    breakpoints: {
      768: {
        slidesPerView: 2.1,
        spaceBetween: 24
      },
      992: {
        slidesPerView: 2.2,
        spaceBetween: 40
      }
    },
    accessibility: {
      containerMessage: en ? 'These are our tariff options' : 'Das sind unsere Tarif-Optionen',
      slideLabelMessage: en ? 'tariff option {{index}} of {{slidesLength}}' : 'Tarif-Option {{index}} von {{slidesLength}}'
    }
  });
});

document.querySelectorAll<HTMLElement>('.slider-app-preview').forEach(slider => {
  if (slider.slider) {
    return;
  }

  const sliderInstance = new Slider({
    sliderContainer: slider,
    showFrom: Breakpoints.Md,
    slidesPerView: 1.1,
    spaceBetween: 20,
    speed: 500,
    pagination: {
      elementSelector: '.slider-pagination'
    },
    navigation: {
      prevButtonSelector: '.button-prev',
      nextButtonSelector: '.button-next',
      disabledClass: 'disabled'
    },
    breakpoints: {
      768: {
        slidesPerView: 1.1,
        spaceBetween: 24
      },
      992: {
        slidesPerView: 1.2,
        spaceBetween: 32
      }
    },
    accessibility: {
      containerMessage: en ? 'Our app offers these advantages' : 'Diese Vorteile bietet unsere App',
      slideLabelMessage: en ? 'element {{index}} of {{slidesLength}}' : 'Element {{index}} von {{slidesLength}}'
    }
  });

  slider.slider = sliderInstance;

  if (slider.slider) {
    const sliderInstance = slider.slider;
    sliderInstance.on('slideChange', () => {
      setOpacity();
    });

    function setOpacity() {
      slider.style.setProperty('--left-opacity', sliderInstance.activeSlideIndex === sliderInstance.slides.length - 1 ? '1' : '0');
      slider.style.setProperty('--right-opacity', sliderInstance.activeSlideIndex !== sliderInstance.slides.length - 1 ? '1' : '0');
    }
  }
});

document.querySelectorAll<HTMLElement>('.slider-full, .swiper-full').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    slidesPerView: 1,
    spaceBetween: 8,
    pagination: {
      elementSelector: '.slider-pagination, .swiper-pagination'
    },
    navigation: {
      prevButtonSelector: '.button-prev',
      nextButtonSelector: '.button-next',
      disabledClass: 'disabled'
    },
    breakpoints: {
      768: {
        spaceBetween: 36
      }
    }
  });
});

document.querySelectorAll<HTMLElement>('.trustpilot-slider, .ekomi-slider').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    slidesPerView: 1.1,
    spaceBetween: 20,
    speed: 500,
    pagination: {
      elementSelector: '.slider-pagination'
    },
    navigation: {
      prevButtonSelector: '.button-prev',
      nextButtonSelector: '.button-next',
      disabledClass: 'disabled'
    },
    breakpoints: {
      768: {
        slidesPerView: 2,
        spaceBetween: 24
      },
      992: {
        slidesPerView: 3,
        spaceBetween: 24
      }
    }
  });
});

document.querySelectorAll<HTMLElement>('.slider-feature-home').forEach(slider => {
  if (slider.slider) {
    return;
  }

  const mobilematch = window.matchMedia('(min-width: 768px)');

  slider.slider = new Slider({
    sliderContainer: slider,
    updateHeight: {
      updateUntil: Breakpoints.Lg
    },
    slidesPerView: 1,
    spaceBetween: 8,
    pagination: {
      elementSelector: '.slider-pagination',
      buttonWrapperClass: 'nav-item',
      buttonWrapperActiveClass: 'active',
      buttonWrapperElement: 'li',
      buttonTexts: [
        mobilematch.matches ? 'Premium Kundensupport' : 'Premium Support',
        mobilematch.matches ? 'Digital und schnell' : 'Digital',
        mobilematch.matches ? 'Fair kalkuliert und flexibel' : 'Fair kalkuliert'
      ]
    },
    breakpoints: {
      768: {
        spaceBetween: 36
      }
    }
  });
});

document.querySelectorAll<HTMLElement>('.slider-feature').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    slidesPerView: 1,
    spaceBetween: 8,
    pagination: {
      elementSelector: '.slider-pagination',
      buttonWrapperClass: 'nav-item',
      buttonWrapperActiveClass: 'active',
      buttonWrapperElement: 'li',
      buttonTexts: ['Faire Tarife', 'Mehr Service', 'Einfach digital']
    },
    breakpoints: {
      768: {
        spaceBetween: 36
      }
    }
  });
});

document.querySelectorAll<HTMLElement>('.trust-slider').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    updateHeight: {
      always: true
    },
    slidesPerView: 1,
    pagination: {
      elementSelector: '.slider-pagination'
    },
    navigation: {
      prevButtonSelector: '.button-prev',
      nextButtonSelector: '.button-next',
      disabledClass: 'disabled'
    },
    customPagination: {
      selector: '.scroll-nav .nav-wrapper',
      activeClass: 'active'
    },
    autoslide: {
      enabled: true
    }
  });
});

document.querySelectorAll<HTMLElement>('.slider-feature-pkv').forEach(slider => {
  if (slider.slider) {
    return;
  }

  const mobilematch = window.matchMedia('(min-width: 768px)');
  slider.slider = new Slider({
    sliderContainer: slider,
    updateHeight: {
      updateUntil: Breakpoints.Lg
    },
    slidesPerView: 1,
    spaceBetween: 8,
    pagination: {
      elementSelector: '.slider-pagination',
      buttonWrapperClass: 'nav-item',
      buttonWrapperActiveClass: 'active',
      buttonWrapperElement: 'li',
      buttonTexts: [
        mobilematch.matches ? 'Premium Kundensupport' : 'Premium Support',
        mobilematch.matches ? 'Digital und schnell' : 'Digital',
        mobilematch.matches ? 'Top-Preis-Leistung' : 'Preis-Leistung',
        mobilematch.matches ? 'Mit Geld-zurück-Garantie' : 'Geld-zurück'
      ]
    },
    breakpoints: {
      768: {
        spaceBetween: 36
      }
    }
  });
});

// INITIALLY MOBILE SLIDERS
document.querySelectorAll<HTMLElement>('.slider-container-md, .swiper-container-md').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    hideFrom: Breakpoints.Md,
    slidesPerView: 1,
    spaceBetween: 24,
    pagination: {
      elementSelector: '.slider-pagination, .swiper-pagination'
    },
    breakpoints: {
      768: {
        slidesPerView: Number(slider.dataset.slidesMd) || 1
      }
    },
    accessibility: {
      slideLabelMessage: en ? 'element {{index}} of {{slidesLength}}' : 'Element {{index}} von {{slidesLength}}'
    }
  });
});

document.querySelectorAll<HTMLElement>('.slider-container-overview').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    hideFrom: Breakpoints.Md,
    slidesPerView: 1.1,
    spaceBetween: 0,
    breakpoints: {
      768: {
        slidesPerView: 2,
        spaceBetween: 20
      }
    },
    accessibility: {
      slideLabelMessage: en ? 'article {{index}} of {{slidesLength}}' : 'Artikel {{index}} von {{slidesLength}}'
    }
  });
});

document.querySelectorAll<HTMLElement>('.friendly-appointment-slider').forEach(slider => {
  if (slider.slider) {
    return;
  }

  slider.slider = new Slider({
    sliderContainer: slider,
    hideFrom: Breakpoints.Sm,
    slidesPerView: 1.3,
    spaceBetween: 8,
    pagination: {
      elementSelector: '.slider-pagination, .swiper-pagination'
    },
    accessibility: {
      slideLabelMessage: en ? 'article {{index}} of {{slidesLength}}' : 'Artikel {{index}} von {{slidesLength}}'
    },
    breakpoints: {
      576: {
        slidesPerView: 2.3,
        spaceBetween: 20
      }
    }
  });
});
